import * as React from "react";
import { SnapshotCollectionsOutcomesHeader } from "./header";
import { SnapshotCollectionsOutcomesRow } from "./row";
import { Ordering } from "@/types/base";
import { SnapshotCollectionsOutcomesWebsocketHandler } from "@/modules/snapshots/snapshotCollections/components/outcomes/websocket-handler";
import { Pagination } from "@/views/drive/pagination";
import { useGetBusinessReportsOutcomeQuery } from "@/modules/snapshots/businessReports.api";
import { SnapshotCollection } from "@/modules/snapshots/snapshotCollection.types";
import { Loader } from "@/components/ui/loader";
import { SnapshotCollectionsOutcomesListInput } from "@/modules/snapshots/snapshotCollections/components/outcomes/list/input";

interface Props {
  selectedCollection: SnapshotCollection;
}

export const SnapshotCollectionsOutcomesList: React.FC<Props> = ({ selectedCollection }) => {
  const [page, setPage] = React.useState(1);
  const [query, setQuery] = React.useState<string>("");
  const [ordering, setOrdering] = React.useState<Ordering>(() => ({ order_by: "status", descending: false }));
  const { data, isLoading, isFetching, refetch, isError } = useGetBusinessReportsOutcomeQuery({
    snapshot_collection_id: selectedCollection.id,
    offset: page - 1,
    query,
    ...ordering,
  });

  React.useEffect(() => {
    setPage(1);
  }, [selectedCollection.id]);

  const reset = () => {
    setPage(1);
    setQuery("");
  };

  if (isError) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="cursor-pointer text-red-500" onClick={refetch}>
          Error loading snapshot collection outcomes
        </p>
      </div>
    );
  }

  if (isLoading || !data) {
    return <Loader />;
  }

  const { results: snapshotCollectionOutcomes = [], total } = data;

  return (
    <div className="mt-4 flow-root">
      <SnapshotCollectionsOutcomesWebsocketHandler
        refetch={refetch}
        selectedCollection={selectedCollection}
        snapshotCollectionOutcomes={snapshotCollectionOutcomes}
      />
      <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <SnapshotCollectionsOutcomesListInput
            isFetching={isFetching}
            setQuery={setQuery}
            query={query}
            reset={reset}
          />
          <div className="overflow-hidden shadow ring-1 ring-black/5 sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <SnapshotCollectionsOutcomesHeader ordering={ordering} setOrdering={setOrdering} />
              <tbody className="divide-y divide-gray-200 bg-white">
                {snapshotCollectionOutcomes.map(businessReportOutcome => (
                  <SnapshotCollectionsOutcomesRow
                    businessReportOutcome={businessReportOutcome}
                    key={businessReportOutcome.id}
                  />
                ))}
                {!snapshotCollectionOutcomes.length && (
                  <tr>
                    <td colSpan={5} className="py-4 text-center text-sm text-gray-500">
                      No snapshots available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <Pagination currentPage={page} totalPages={Math.ceil(total / 10)} onPageChange={setPage} />
        </div>
      </div>
    </div>
  );
};
